import { getContentfulClient } from 'graphql/clients';
import { contentfulRESTClient } from 'rest/clients';
import { getArticleIdsForCategory, getTagIdByTagName } from 'data/global/data';
import {
  articleCardTransformer,
  miniArticleCardTransformer,
} from 'data/global/transformer';
import { categoryPageTransformer } from 'data/CategoryPage/transformer';
import {
  CategorySlugs,
  SingleCategoryQuery as SingleCategoryQueryType,
  CategoryListQuery as CategoryListQueryType,
  TransformedAssemblyType,
  CategoryWithChildren,
} from './types';
import { ArticleCard, MiniArticleCard } from 'data/global/types';
import { CategoryArticlesById as CategoryArticlesByIdType } from 'graphql/__generated__/operations/CategoryArticlesById';
import { CategoryMiniArticleById as CategoryMiniArticleByIdType } from 'graphql/__generated__/operations/CategoryMiniArticleById';
import { injectValueIntoArray } from 'utils/injectValueIntoArray';
import gql from 'graphql-tag';
import { ArticleCardFragment } from 'data/global/fragments/ArticleCardFragment';
import { MiniArticleCardFragment } from 'data/global/fragments/MiniArticleCardFragment';
import { ArticleSetType } from 'components/CategoryChildArticles/CategoryChildArticles';
import { setShortDescriptionFallback } from 'data/SingleArticle/helpers';

const CategoryListQuery = gql`
  query CategoryListQuery {
    categoryAssemblyCollection {
      items {
        slug
        category {
          sys {
            id
          }
          title
          visibleToPublic
          order
          parentCategory {
            sys {
              id
            }
          }
        }
      }
    }
  }
`;
const SingleCategoryQuery = gql`
  query SingleCategoryQuery($slug: String!) {
    categoryAssemblyCollection(where: { slug: $slug }, limit: 1) {
      items {
        metadata {
          seoTitle
          seoDescription
          socialImage {
            image {
              url
            }
          }
        }
        category {
          title
          linkedFrom {
            categoryTopicCollection {
              items {
                title
                linkedFrom {
                  categoryAssemblyCollection(limit: 1) {
                    items {
                      slug
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const CategoryMiniArticlesById = gql`
  ${MiniArticleCardFragment}
  query CategoryMiniArticleById($articleIds: [String]!) {
    articleCollection(
      where: { sys: { id_in: $articleIds }, discoverable: true }
    ) {
      ...MiniArticleCardFragment
    }
  }
`;
const CategoryArticlesById = gql`
  ${ArticleCardFragment}
  query CategoryArticlesById($articleIds: [String]!) {
    articleCollection(
      where: { sys: { id_in: $articleIds }, discoverable: true }
    ) {
      ...ArticleCardFragment
    }
  }
`;

const FEATURED_CATEGORY_TAG = 'Featured - category';
const FEATURED_ARTICLE_COUNT = 2;
const ARTICLE_CARD_COUNT = 5;
const MINI_ARTICLE_CARD_COUNT = 5;
const SUBSCRIBE_CARD_POSITION = 4;

export async function getAllCategorySlugs(): Promise<CategorySlugs> {
  const ContentfulClient = getContentfulClient(false);
  const {
    data: { categoryAssemblyCollection },
    errors,
  } = await ContentfulClient.query<CategoryListQueryType>({
    query: CategoryListQuery,
  });
  if (errors?.length) {
    throw Error(`Something went wrong!`);
  }
  if (!categoryAssemblyCollection?.items) {
    throw Error('No data found in categoryAssemblyCollection GQL query!');
  }
  // only show parent categories as a possible route
  const items = categoryAssemblyCollection.items
    .filter((item) => item != null)
    .filter((item) => !item?.category?.parentCategory)
    .map((item) => ({
      slug: item?.slug ?? '',
      title: item?.category?.title ?? '',
    }));

  return items;
}

export async function fetchCategoriesForNavigation(): Promise<
  CategoryWithChildren[]
> {
  const ContentfulClient = getContentfulClient(false);
  const {
    data: { categoryAssemblyCollection },
    errors,
  } = await ContentfulClient.query<CategoryListQueryType>({
    query: CategoryListQuery,
  });
  if (errors?.length) {
    throw Error('Something went wrong getting categories for navigation!');
  }
  if (!categoryAssemblyCollection?.items) {
    throw Error('No data found in categoryAssemblyCollection GQL query!');
  }

  const getCategoryChildren = (id?: string) => {
    return categoryAssemblyCollection.items
      .filter(
        (item) =>
          item != null &&
          item?.category?.visibleToPublic &&
          item?.category?.parentCategory?.sys?.id === id,
      )
      .sort((a, b) => (a?.category?.order ?? 1) - (b?.category?.order ?? 1))
      .map((item) => ({ slug: item?.slug, title: item?.category?.title }));
  };

  const items = categoryAssemblyCollection.items
    .filter(
      (item) =>
        item != null &&
        item?.category?.visibleToPublic &&
        !item?.category?.parentCategory,
    )
    .sort((a, b) => (a?.category?.order ?? 1) - (b?.category?.order ?? 1))
    .map((item) => ({
      slug: item?.slug,
      title: item?.category?.title,
      children: getCategoryChildren(item?.category?.sys?.id),
    }));

  return items;
}

export async function getSingleCategoryQuery(
  slug: string,
): Promise<TransformedAssemblyType> {
  const ContentfulClient = getContentfulClient(false);
  const { data } = await ContentfulClient.query<SingleCategoryQueryType>({
    query: SingleCategoryQuery,
    variables: {
      slug,
    },
    errorPolicy: 'none',
  });

  if (!data?.categoryAssemblyCollection?.items[0]) {
    throw Error(`CategoryPage for ${slug} information not found`);
  }
  // only show parent categories as a possible route
  const category = data?.categoryAssemblyCollection?.items[0];
  return categoryPageTransformer(category);
}

async function categoryArticlesById(
  articleIds: string[],
): Promise<ArticleCard[]> {
  const ContentfulClient = getContentfulClient(false);
  const {
    data: { articleCollection },
    errors,
  } = await ContentfulClient.query<CategoryArticlesByIdType>({
    query: CategoryArticlesById,
    variables: {
      articleIds,
    },
  });

  if (errors?.length) {
    throw Error('Something went wrong getting category articles!');
  }

  if (!articleCollection?.items) {
    throw Error('No data found in categoryAssemblyCollection GQL query!');
  }

  const articles = articleCollection?.items || [];

  const transformedArticles = articles
    .filter((article) => !!article)
    .map((article) => articleCardTransformer(article));

  return transformedArticles;
}

/** ARTICLE CARDS
 * Use the SDK to fetch the IDs for articles
 * in this category, sorted correctly by date
 */
export async function getCategoryArticles(
  categoryAssemblySlug: string = '',
  excludeIds: string = '',
): Promise<(ArticleCard | null)[]> {
  if (!categoryAssemblySlug) {
    throw Error('No category assembly slug');
  }

  const articleIds = await getArticleIdsForCategory({
    slug: categoryAssemblySlug,
    limit: ARTICLE_CARD_COUNT,
    excludeIds,
  });

  let articles: ArticleCard[] = await categoryArticlesById(articleIds);
  articles = await setShortDescriptionFallback(articles);

  /**
   * Insert `null` in the SUBSCRIBE_CARD_POSITION so ArticleGrid can show the subscribe card
   */
  return injectValueIntoArray(articles, SUBSCRIBE_CARD_POSITION, null);
}

/** Use GraphQL to fetch featured article data for the IDs retrieved
 * from getCategoryFeaturedArticleIds() above.
 */
export async function getFeaturedCategoryArticles(
  categorySlug?: String | null,
): Promise<ArticleCard[]> {
  const categoryTopic = await contentfulRESTClient.getEntries<{
    category: { sys: { id: string } };
  }>({
    limit: 1,
    skip: 0,
    content_type: 'categoryAssembly',
    'fields.slug': categorySlug,
    select: 'fields.category',
  });

  const categoryTopicSysId = categoryTopic.items[0].fields.category.sys.id;
  const featuredCategoryTagId = await getTagIdByTagName(FEATURED_CATEGORY_TAG);

  const { items } = await contentfulRESTClient.getEntries({
    limit: FEATURED_ARTICLE_COUNT,
    content_type: 'article',
    'fields.categories.sys.id': categoryTopicSysId,
    'fields.tags.sys.id': featuredCategoryTagId,
    'fields.discoverable': true,
    select: 'sys.id',
    order: '-fields.editorialLastPublishedDateComputed',
  });

  const articleIds = items.map((id: { sys: { id: string } }) => {
    return id.sys.id;
  });

  const articles = await categoryArticlesById(articleIds);

  const articlesWithFallback = await setShortDescriptionFallback(articles);

  return articlesWithFallback;
}

type ChildCategoryIdInputType = {
  slug: string;
  title: string;
};

/** MINI ARTICLES
 * Get all the child category article data needed for the child category components
 */
async function getMiniArticlesCards(
  articleIds: string[],
): Promise<MiniArticleCard[]> {
  const ContentfulClient = getContentfulClient(false);
  const {
    data: { articleCollection },
    errors,
  } = await ContentfulClient.query<CategoryMiniArticleByIdType>({
    query: CategoryMiniArticlesById,
    variables: {
      articleIds,
    },
  });
  if (errors?.length) {
    throw Error(`Something went wrong!`);
  }
  if (!articleCollection?.items) {
    throw Error('No data found in categoryAssemblyCollection GQL query!');
  }

  const articles = articleCollection?.items || [];

  const transformedArticles = articles
    .filter((article) => !!article)
    .map((article) => miniArticleCardTransformer(article));
  return transformedArticles;
}

export async function getArticleSetsFromChildCategoryIds(
  childCategoryIds: ChildCategoryIdInputType[],
  setExcludeIds: string = '',
  featuredArticles?: ArticleCard[],
): Promise<ArticleSetType[]> {
  const childCategoryArticleIds = childCategoryIds
    ? await Promise.all(
        childCategoryIds
          .filter((c) => c)
          .map(async ({ slug, title }) => {
            const articleIds = await getArticleIdsForCategory({
              slug,
              limit: MINI_ARTICLE_CARD_COUNT,
              excludeIds: setExcludeIds,
            });
            return {
              category: { title, slug },
              articleIds,
            };
          }),
      )
    : [];

  const articleSets = await Promise.all(
    childCategoryArticleIds.map(async ({ category, articleIds }) => ({
      category,
      articles: await getMiniArticlesCards(articleIds),
    })),
  );
  return articleSets
    .filter(
      (articleSet) =>
        articleSet?.articles?.length &&
        articleSet?.category?.slug !== undefined,
    )
    .map((set) => {
      return {
        ...set,
        slugsToDedupe: [
          ...(featuredArticles?.length
            ? featuredArticles.map((article) => article?.slug)
            : []),
          ...articleSets.flatMap((set) =>
            set?.articles?.map((article) => article?.slug),
          ),
        ].filter(Boolean),
      };
    });
}
